<template>
  <div class="domain-distribution-page">
    <div class="page-heading">
      <h1>Domain Distribution</h1>
      <!-- <div class="latest-update">
        <span>Latest Update: {{ latestUpdate || '-' }}</span>
      </div> -->
    </div>

    <section class="domain-card district-filter-card">
      <h2>Filter</h2>
      <div class="district-filter-row">
        <div class="filter-multiselect-group">
          <!-- Province Dropdown -->
          <div class="district-dropdown" ref="provinceDropdown">
            <div class="district-dropdown-control" :class="{ active: openDropdowns.province }">
              <input v-model="searchInputs.province" type="text" :placeholder="provincePlaceholderText"
                @focus="openDropdown('province')" @input="handleSearchInput('province')" />
              <button type="button" class="district-chevron" @click="toggleDropdown('province')">
                <i class="fas fa-chevron-down"></i>
              </button>
            </div>
            <div v-if="openDropdowns.province" class="district-dropdown-menu">
              <button v-for="opt in filteredProvinceOptions" :key="typeof opt === 'object' ? opt.value : opt"
                type="button" class="district-dropdown-item" @click="selectProvinceOption(opt)">
                {{ typeof opt === 'object' ? opt.label : opt }}
              </button>
              <div v-if="filteredProvinceOptions.length === 0" class="district-dropdown-empty">
                No province found
              </div>
            </div>
          </div>

          <!-- Regency Dropdown -->
          <div class="district-dropdown" :class="{ disabled: !filter.province }" ref="regencyDropdown">
            <div class="district-dropdown-control"
              :class="{ active: openDropdowns.regency, disabled: !filter.province }">
              <input v-model="searchInputs.regency" type="text" :placeholder="regencyPlaceholder"
                :disabled="!filter.province" @focus="openDropdown('regency')" @input="handleSearchInput('regency')" />
              <button type="button" class="district-chevron" :disabled="!filter.province"
                @click="toggleDropdown('regency')">
                <i class="fas fa-chevron-down"></i>
              </button>
            </div>
            <div v-if="openDropdowns.regency && filter.province" class="district-dropdown-menu">
              <button v-for="opt in filteredRegencyOptions" :key="typeof opt === 'object' ? opt.value : opt"
                type="button" class="district-dropdown-item" @click="selectRegencyOption(opt)">
                {{ typeof opt === 'object' ? opt.label : opt }}
              </button>
              <div v-if="filteredRegencyOptions.length === 0" class="district-dropdown-empty">
                No regency found
              </div>
            </div>
          </div>

          <!-- District Dropdown -->
          <div class="district-dropdown" :class="{ disabled: !filter.regency }" ref="districtDropdown">
            <div class="district-dropdown-control"
              :class="{ active: openDropdowns.district, disabled: !filter.regency }">
              <input v-model="searchInputs.district" type="text" :placeholder="districtPlaceholderText"
                :disabled="!filter.regency" @focus="openDropdown('district')" @input="handleSearchInput('district')" />
              <button type="button" class="district-chevron" :disabled="!filter.regency"
                @click="toggleDropdown('district')">
                <i class="fas fa-chevron-down"></i>
              </button>
            </div>
            <div v-if="openDropdowns.district && filter.regency" class="district-dropdown-menu">
              <button v-for="opt in filteredDistrictSelectOptions" :key="typeof opt === 'object' ? opt.value : opt"
                type="button" class="district-dropdown-item" @click="selectDistrictOption(opt)">
                {{ typeof opt === 'object' ? opt.label : opt }}
              </button>
              <div v-if="filteredDistrictSelectOptions.length === 0" class="district-dropdown-empty">
                No district found
              </div>
            </div>
          </div>
        </div>
        <button type="button" class="reset-filter-btn" :class="{ active: hasActiveDistrictFilter }"
          :disabled="!hasActiveDistrictFilter" @click="resetFilter">
          <i class="fas fa-redo-alt"></i>
          <span>Reset</span>
        </button>
      </div>
    </section>

    <section class="domain-card radius-table-card">
      <h2>Radius Table (Cluster)</h2>
      <div class="radius-table-wrapper">
        <table class="radius-table">
          <thead>
            <tr>
              <th>Radius</th>
              <th>Total Scan</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in filteredRadiusRows" :key="row.radius || idx">
              <td>{{ formatRadiusDisplayLabel(row.radius) }}</td>
              <td>{{ row.totalScans }}</td>
              <td>{{ row.percentage }}</td>
            </tr>
            <tr v-if="filteredRadiusRows.length === 0">
              <td colspan="3" class="empty-state">{{ radiusTableEmptyText }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="domain-card map-card">
      <h2>Distribution Map (Cluster)</h2>
      <div class="map-wrapper" :class="{ 'map-wrapper--no-data': !hasDistributionMapData }">
        <GmapMap ref="distributionMap" :center="mapCenter" :zoom="mapZoom" :options="distributionMapOptions"
          class="distribution-map">
          <gmap-marker v-for="label in visibleMapNumberLabels" :key="label.id"
            :position="{ lat: label.lat, lng: label.lng }" :icon="numberMarkerIcon" :label="label.markerLabel"
            :z-index="label.zIndex" :clickable="false" :draggable="false"></gmap-marker>
        </GmapMap>
        <div v-if="mapStateText" class="map-state">{{ mapStateText }}</div>
        <div v-if="showMapLegend" class="map-legend" :class="{ collapsed: isMapLegendCollapsed }">
          <div v-if="!isMapLegendCollapsed" class="map-legend-grid">
            <button v-for="item in visibleMapLegendItems" :key="item.key" type="button" class="map-legend-item"
              :class="{ checked: item.checked }" @click="toggleMapLegend(item.key)">
              <span class="legend-check"></span>
              <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
              <span>{{ item.label }}</span>
            </button>
          </div>
          <button type="button" class="legend-collapse"
            :aria-label="isMapLegendCollapsed ? 'Open legend' : 'Close legend'" @click.stop="toggleLegendCollapse">
            <i :class="isMapLegendCollapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'"></i>
          </button>
        </div>

        <!-- Ring Details Side Modal -->
        <transition name="fade-slide-left">
          <div v-if="activeRingModal" class="ring-modal-sidebar">
            <div class="ring-modal-header">
              <div class="ring-modal-title-row">
                <div class="ring-modal-title-left">
                  <span class="ring-color-dot" :style="{ backgroundColor: activeRingModal.color }"></span>
                  <span class="ring-modal-title">Domain di Ring {{ activeRingModal.ringNumber }}</span>
                </div>
                <button type="button" class="ring-modal-close-btn" @click="closeRingModal">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div class="ring-modal-subtitle">
                {{ formatRadiusDisplayLabel(activeRingModal.label) }} &middot; {{ formatNumber(activeRingModal.totalScans) }} scan &middot; {{
                  formatNumber(activeRingModal.totalDomains) }} domain
              </div>
              <div class="ring-modal-search-box">
                <i class="fas fa-search ring-modal-search-icon"></i>
                <input v-model="activeRingModal.search" type="text" placeholder="Cari domain..."
                  class="ring-modal-search-input" @input="handleRingSearchInput" />
              </div>
              <div class="ring-modal-sort-row">
                <span class="ring-modal-sort-label">Urutkan:</span>
                <span class="ring-modal-sort-btn" @click="toggleRingSort">
                  {{ activeRingModal.sort === 'desc' ? 'Scan terbanyak ↑' : 'Scan paling sedikit ↓' }}
                </span>
              </div>
            </div>

            <div class="ring-modal-body">
              <div v-if="activeRingModal.loading" class="ring-modal-loading">
                <i class="fas fa-spinner fa-spin"></i> Loading...
              </div>
              <template v-else>
                <div v-if="activeRingModal.list.length === 0" class="ring-modal-empty">
                  Tidak ada domain ditemukan
                </div>
                <div v-else class="ring-modal-list">
                  <div v-for="item in activeRingModal.list" :key="item.id || item.domain" class="ring-modal-item">
                    <div class="ring-modal-item-info">
                      <div class="ring-modal-domain-name">{{ item.domain }}</div>
                      <div class="ring-modal-domain-scans">{{ item.scans }} scan</div>
                    </div>
                    <div class="ring-modal-item-badge">
                      <span v-if="getStatusBadgeClass(item.status)" :class="getStatusBadgeClass(item.status)">
                        {{ item.status }}
                      </span>
                      <span v-else class="status-badge-text">{{ item.status }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </transition>
      </div>
    </section>

    <section class="domain-card radius-table-card">
      <div class="card-header-row">
        <h2>Domain List</h2>
        <div class="domain-header-controls">
          <div class="domain-search-wrapper">
            <input v-model="domainListSearch" type="text" placeholder="Search Domain" class="domain-search-input" />
            <i class="fas fa-search domain-search-icon"></i>
          </div>

          <!-- Status Dropdown (only visible when data/district active) -->
          <div v-if="selectedDistrict || filter.district" ref="statusDropdown"
            class="district-dropdown status-filter-dropdown">
            <div class="district-dropdown-control" :class="{ active: openDropdowns.status }"
              @click="toggleDropdown('status')">
              <input :value="filter.domain_status || 'All Status'" type="text" readonly placeholder="All Status"
                class="status-select-input" />
              <button type="button" class="district-chevron">
                <i class="fas fa-chevron-down"></i>
              </button>
            </div>
            <div v-if="openDropdowns.status" class="district-dropdown-menu">
              <button v-for="opt in statusOptions" :key="opt" type="button" class="district-dropdown-item"
                :class="{ active: (filter.domain_status || 'All Status') === opt }"
                @click.stop="selectStatusOption(opt)">
                {{ opt }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="radius-table-wrapper">
        <table class="radius-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Total Scan</th>
              <th>First Scan</th>
              <th>Risk Score</th>
              <th>Domain Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredDomainRows" :key="row.domain || row.id">
              <td class="domain-cell">{{ row.domain }}</td>
              <td>{{ row.totalScans }}</td>
              <td>{{ row.firstScan }}</td>
              <td>
                <span v-if="getRiskBadgeClass(row.riskScore)" :class="getRiskBadgeClass(row.riskScore)">
                  {{ row.riskScore }}
                </span>
                <span v-else class="status-badge-text">{{ row.riskScore }}</span>
              </td>
              <td>
                <span v-if="getStatusBadgeClass(row.status)" :class="getStatusBadgeClass(row.status)">
                  {{ row.status }}
                </span>
                <span v-else class="status-badge-text">{{ row.status }}</span>
              </td>
              <td class="action-cell">
                <button v-if="!isWatchlisted(row.status)" type="button" class="btn-action-watchlist"
                  @click="handleWatchlist(row)">
                  Watchlist
                </button>
                <button type="button" class="btn-action-detail" @click="handleDetail(row)">
                  Detail
                </button>
              </td>
            </tr>
            <tr v-if="filteredDomainRows.length === 0">
              <td colspan="6" class="empty-state domain-empty-state">{{ domainTableEmptyText }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="selectedDistrict || filter.district" class="domain-table-footer row mt-3 align-items-center">
        <div class="col-12 d-flex align-items-center justify-content-between flex-wrap">
          <div class="d-flex align-items-center">
            <label style="margin-right: 8px; margin-bottom: 0; color: #cbcbcb; font-size: 14px;">View</label>
            <select v-model="domainListLimit" class="form-control form-control-sm"
              style="width: auto; display: inline-block; padding: 4px 8px;" @change="onDomainListLimitChange">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
            <span class="items-per-page-text">items per page</span>
          </div>
          <div class="d-flex align-items-center flex-wrap">
            <p class="mt-2 mb-2" style="color: #cbcbcb; margin-right: 20px; margin-bottom: 0;">
              Showing {{ showingStartItem }}-{{ showingEndItem }} of {{ domainListTotal }} Items
            </p>
            <nav class="pagination-minimal">
              <ul class="pagination" style="margin: 0; flex-wrap: wrap;">
                <li class="page-item" :class="{ disabled: domainListCurrentPage <= 1 }"
                  @click.stop.prevent="changeDomainListPage('prev')">
                  <a class="page-link" href="javascript:void(0)">&lsaquo;</a>
                </li>
                <li class="page-item" :class="{ disabled: domainListCurrentPage >= domainListMaxPage }"
                  @click.stop.prevent="changeDomainListPage('next')">
                  <a class="page-link" href="javascript:void(0)">&rsaquo;</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  metaInfo: {
    title: "Domain Distribution",
  },
  data() {
    return {
      latestUpdate: null,
      filter: {
        domain: "All Domains",
        province: null,
        regency: null,
        district: null,
        domain_status: null,
      },
      searchInputs: {
        domain: "All Domains",
        province: "",
        regency: "",
        district: "",
      },
      openDropdowns: {
        domain: false,
        province: false,
        regency: false,
        district: false,
        status: false,
      },
      options: {
        domains: ["All Domains"],
      },
      statusOptions: ["All Status", "Under Review", "Watchlisted"],
      provinceOptions: [],
      regencyOptions: [],
      districtOptions: [],
      isProvinceLoading: false,
      isRegencyLoading: false,
      isDistrictLoading: false,
      selectedProvinceCode: null,
      selectedRegencyCode: null,
      selectedDistrict: null,
      districtSearch: "",
      showDistrictDropdown: false,
      isRadiusLoading: false,
      radiusRequestToken: 0,
      isMapLoading: false,
      mapRequestToken: 0,
      geoJsonLayer: null,
      circleOverlays: [],
      provinceGeoJson: null,
      detailGeoJsonCache: {},
      isMapLegendCollapsed: false,
      radiusRows: [],
      domainRows: [],
      domainListSearch: "",
      domainListSearchTimer: null,
      isDomainLoading: false,
      domainRequestToken: 0,
      domainListCurrentPage: 1,
      domainListLimit: 10,
      domainListMaxPage: 1,
      domainListTotal: 0,
      activeRingModal: null,
      ringSearchTimer: null,
      markers: [],
      radiusCenter: null,
      isApplyingFromQuery: false,
      mapFeatureLabels: [],
      mapLegendItems: [
        { key: "location", label: "Location", color: "#CD2E2C", checked: true },
        { key: "0-500", label: "0 m - 500 m", color: "#CD2E2C", checked: true },
        { key: "500-1", label: "> 500 m - 1 km", color: "#1E3A8A", checked: true },
        { key: "1-2", label: "> 1 km - 2 km", color: "#F59E0B", checked: true },
        { key: "2-5", label: "> 2 km - 5 km", color: "#F97316", checked: true },
        { key: "5-plus", label: "> 5 km - 10 km", color: "#D1D5DB", checked: true },
      ],
      defaultCenter: {
        lat: -2.548926,
        lng: 118.0148634,
      },
      gMapOptions: {
        zoomControl: true,
        panControl: false,
        cameraControl: false,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        keyboardShortcuts: true,
        disableDefaultUi: false,
        styles: [
          {
            featureType: "poi",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "landscape",
            stylers: [{ saturation: -70 }, { lightness: 35 }],
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ color: "#dfe7f1" }, { weight: 1.3 }],
          },
          {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [{ color: "#9fb5d2" }, { weight: 1.7 }],
          },
          {
            featureType: "road.highway",
            elementType: "labels.text.fill",
            stylers: [{ color: "#3f4a5a" }],
          },
          {
            featureType: "administrative",
            elementType: "geometry.stroke",
            stylers: [{ color: "#6d7280" }, { weight: 1.1 }],
          },
          {
            featureType: "transit",
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "water",
            stylers: [{ color: "#c8dff3" }],
          },
        ],
      },
    };
  },
  computed: {
    showingStartItem() {
      if (this.domainListTotal === 0) return 0;
      return (this.domainListCurrentPage - 1) * this.domainListLimit + 1;
    },
    showingEndItem() {
      const end = this.domainListCurrentPage * this.domainListLimit;
      return end > this.domainListTotal ? this.domainListTotal : end;
    },
    districtPlaceholder() {
      return this.isDistrictLoading ? "Loading District..." : "Select District";
    },
    provincePlaceholderText() {
      return this.isProvinceLoading ? "Loading Province..." : "Select Province";
    },
    regencyPlaceholder() {
      if (this.isRegencyLoading) return "Loading Regency...";
      return this.filter.province ? "Select Regency" : "Select province first";
    },
    districtPlaceholderText() {
      if (this.isDistrictLoading) return "Loading District...";
      return this.filter.regency ? "Select District" : "Select region first";
    },
    filteredDistrictOptions() {
      const search = this.districtSearch.toLowerCase().trim();
      if (!search || this.selectedDistrict) return this.districtOptions;

      return this.districtOptions.filter((district) =>
        (district.label || district).toLowerCase().includes(search)
      );
    },
    filteredDomainOptions() {
      const search = (this.searchInputs.domain || "").toLowerCase().trim();
      if (!search || search === (this.filter.domain || "").toLowerCase()) {
        return this.options.domains;
      }
      return this.options.domains.filter((opt) => opt.toLowerCase().includes(search));
    },
    filteredProvinceOptions() {
      const search = (this.searchInputs.province || "").toLowerCase().trim();
      if (!search || search === (this.filter.province || "").toLowerCase()) {
        return this.provinceOptions;
      }
      return this.provinceOptions.filter((opt) => {
        const label = typeof opt === "object" ? opt.label : opt;
        return label.toLowerCase().includes(search);
      });
    },
    filteredRegencyOptions() {
      const search = (this.searchInputs.regency || "").toLowerCase().trim();
      if (!search || search === (this.filter.regency || "").toLowerCase()) {
        return this.regencyOptions;
      }
      return this.regencyOptions.filter((opt) => {
        const label = typeof opt === "object" ? opt.label : opt;
        return label.toLowerCase().includes(search);
      });
    },
    filteredDistrictSelectOptions() {
      const search = (this.searchInputs.district || "").toLowerCase().trim();
      if (!search || search === (this.filter.district || "").toLowerCase()) {
        return this.districtOptions;
      }
      return this.districtOptions.filter((opt) => {
        const label = typeof opt === "object" ? opt.label : opt;
        return label.toLowerCase().includes(search);
      });
    },
    hasActiveDistrictFilter() {
      return Boolean(
        (this.filter.domain && this.filter.domain !== "All Domains") ||
        this.filter.province ||
        this.filter.regency ||
        this.filter.district ||
        this.selectedDistrict ||
        this.districtSearch.trim()
      );
    },
    radiusTableEmptyText() {
      if (this.isRadiusLoading) return "Loading radius distribution...";
      if (this.selectedDistrict) return "No radius distribution data";
      return "Select a district to display data";
    },
    filteredDomainRows() {
      if (!this.selectedDistrict && !this.filter.district) return [];
      let rows = this.domainRows;

      const selectedStatus = this.filter.domain_status;
      if (selectedStatus && selectedStatus !== "All Status") {
        const normSelected = String(selectedStatus).toLowerCase().trim();
        rows = rows.filter((row) => {
          const normRowStatus = String(row.status || "").toLowerCase().trim();
          return normRowStatus.includes(normSelected) || normSelected.includes(normRowStatus);
        });
      }

      const search = (this.domainListSearch || "").toLowerCase().trim();
      if (!search) return rows;
      return rows.filter((row) =>
        (row.domain || "").toLowerCase().includes(search)
      );
    },
    domainTableEmptyText() {
      if (this.isDomainLoading) return "Loading domain list...";
      if (this.selectedDistrict || this.filter.district) return "No domain list data";
      return "Select a district to display data";
    },
    mapStateText() {
      if (this.isMapLoading) return "Loading distribution map...";
      if (!this.selectedDistrict) return "";
      if (!this.hasDistributionMapData) {
        return "No distribution map data";
      }
      return "";
    },
    hasDistributionMapData() {
      return Boolean(
        this.selectedDistrict &&
        !this.isMapLoading &&
        (this.filteredMarkers.length > 0 ||
          this.mapFeatureLabels.length > 0 ||
          this.radiusCenter ||
          this.geoJsonLayer)
      );
    },
    distributionMapOptions() {
      if (!this.hasDistributionMapData) {
        return {
          ...this.gMapOptions,
          disableDefaultUi: true,
          zoomControl: false,
          panControl: false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          keyboardShortcuts: false,
          draggable: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          gestureHandling: "none",
        };
      }

      return {
        ...this.gMapOptions,
        disableDefaultUi: false,
        zoomControl: true,
        cameraControl: false,
        keyboardShortcuts: true,
        draggable: true,
        scrollwheel: true,
        disableDoubleClickZoom: false,
        gestureHandling: "auto",
      };
    },
    numberMarkerIcon() {
      if (!window.google) return null;

      return {
        url: "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=",
        scaledSize: new window.google.maps.Size(1, 1),
        labelOrigin: new window.google.maps.Point(0, 0),
      };
    },
    visibleMapNumberLabels() {
      if (this.mapFeatureLabels.length > 0) {
        return this.mapFeatureLabels.filter((label) => this.isMapLegendChecked(label.bucketKey));
      }

      return this.mapNumberLabels.filter((label) => this.isMapLegendChecked(label.bucketKey));
    },
    visibleMapLegendItems() {
      return this.mapLegendItems.filter((item) => item.key !== "location");
    },
    mapNumberLabels() {
      if (!this.selectedDistrict || this.markers.length === 0) return [];

      if (this.markers.some((marker) => marker.bucketKey)) {
        const bucketOrders = {
          "0-500": 1,
          "500-1": 2,
          "1-2": 3,
          "2-5": 4,
          "5-plus": 5,
        };

        return this.markers.map((marker) => {
          const text = String(marker.totalScans || marker.location || 0);

          return {
            id: marker.id,
            bucketKey: marker.bucketKey,
            lat: marker.lat,
            lng: marker.lng,
            zIndex: 1000 + (bucketOrders[marker.bucketKey] || 0),
            markerLabel: {
              text,
              color: this.getMarkerLabelColor(marker.bucketKey),
              fontSize: text.length > 2 ? "12px" : "14px",
              fontWeight: "700",
            },
          };
        });
      }

      const center = this.getMarkerCenter(this.markers);
      const buckets = this.markers.reduce((acc, marker) => {
        const bucket = this.getRadiusBucket(marker, center);
        const gridSize = this.getBucketGridSize(bucket.key);
        const latKey = Math.floor(marker.lat / gridSize);
        const lngKey = Math.floor(marker.lng / gridSize);
        const key = `${bucket.key}-${latKey}-${lngKey}`;

        if (!acc[key]) {
          acc[key] = {
            id: key,
            bucketKey: bucket.key,
            color: bucket.color,
            count: 0,
            lat: 0,
            lng: 0,
          };
        }

        acc[key].count += 1;
        acc[key].lat += marker.lat;
        acc[key].lng += marker.lng;
        return acc;
      }, {});

      return Object.values(buckets).map((bucket) => {
        const text = String(bucket.count);

        return {
          ...bucket,
          lat: bucket.lat / bucket.count,
          lng: bucket.lng / bucket.count,
          markerLabel: {
            text,
            color: bucket.color,
            fontSize: text.length > 2 ? "12px" : "14px",
            fontWeight: "700",
          },
        };
      });
    },
    filteredRadiusRows() {
      if (!this.selectedDistrict) return [];
      return this.radiusRows.filter((row) => row.district === this.selectedDistrict);
    },
    filteredMarkers() {
      if (!this.selectedDistrict) return [];
      return this.markers;
    },
    isGeoJsonVisible() {
      return this.mapLegendItems.some((item) => item.checked);
    },
    mapCenter() {
      if (this.radiusCenter) return this.radiusCenter;

      if (this.mapFeatureLabels.length > 0) {
        const totals = this.mapFeatureLabels.reduce(
          (acc, label) => {
            acc.lat += label.lat;
            acc.lng += label.lng;
            return acc;
          },
          { lat: 0, lng: 0 }
        );

        return {
          lat: totals.lat / this.mapFeatureLabels.length,
          lng: totals.lng / this.mapFeatureLabels.length,
        };
      }

      if (this.filteredMarkers.length === 0) return this.defaultCenter;

      const totals = this.filteredMarkers.reduce(
        (acc, marker) => {
          acc.lat += marker.lat;
          acc.lng += marker.lng;
          return acc;
        },
        { lat: 0, lng: 0 }
      );

      return {
        lat: totals.lat / this.filteredMarkers.length,
        lng: totals.lng / this.filteredMarkers.length,
      };
    },
    mapZoom() {
      return this.radiusCenter || this.filteredMarkers.length > 0 || this.mapFeatureLabels.length > 0 ? 12 : 5;
    },
    showMapLegend() {
      return Boolean(this.selectedDistrict && (this.radiusCenter || this.geoJsonLayer || this.filteredMarkers.length > 0));
    },
  },
  watch: {
    hasDistributionMapData() {
      this.syncDistributionMapOptions();
    },
    'filter.province'(newProv) {
      if (this.isApplyingFromQuery) return;
      if (!newProv) return;
      const validRegencies = this.regencyOptions.map((r) => typeof r === "object" ? r.label : r);
      if (this.filter.regency) {
        if (!validRegencies.includes(this.filter.regency)) {
          this.filter.regency = null;
          this.searchInputs.regency = "";
        }
      }
    },
    'filter.regency'(newReg) {
      if (this.isApplyingFromQuery) return;
      if (!newReg) return;
      const validDistricts = this.districtOptions.map((d) => typeof d === "object" ? d.label : d);
      if (this.filter.district) {
        if (!validDistricts.includes(this.filter.district)) {
          this.filter.district = null;
          this.searchInputs.district = "";
          this.onDistrictSelect(null);
        }
      }
    },
    domainListSearch() {
      if (this.selectedDistrict) {
        if (this.domainListSearchTimer) clearTimeout(this.domainListSearchTimer);
        this.domainListSearchTimer = setTimeout(() => {
          this.domainListCurrentPage = 1;
          this.fetchDomainList(this.selectedDistrict);
        }, 300);
      }
    },
  },
  async mounted() {
    this.fetchProvinceOptions();
    document.addEventListener("click", this.handleDocumentClick);
    this.$nextTick(this.syncDistributionMapOptions);
    await this.applyFilterFromQuery();
    if (!this.selectedDistrict && !this.filter.district) {
      this.fetchDomainList();
    }
    this.fetchLatestUpdate();
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
    this.clearGeoJsonLayer();
    this.clearCircleOverlays();
  },
  methods: {
    fetchProvinceOptions() {
      this.isProvinceLoading = true;

      return this.$http
        .get("/api/master/provinsi")
        .then((resp) => {
          const data = resp.data && resp.data.data ? resp.data.data : [];

          this.provinceOptions = data.map((province) => ({
            label: province.nama_provinsi || province.name || province.label,
            value: province.kode_provinsi || province.id || province.code || province.value,
          }));
        })
        .catch(() => {
          this.provinceOptions = [];
        })
        .finally(() => {
          this.isProvinceLoading = false;
        });
    },
    fetchRegencyOptions(kodeProvinsi) {
      if (!kodeProvinsi) {
        this.regencyOptions = [];
        return Promise.resolve();
      }

      this.isRegencyLoading = true;

      return this.$http
        .get("/api/master/kotakabupaten", {
          params: { kode_provinsi: kodeProvinsi },
        })
        .then((resp) => {
          const data = resp.data && resp.data.data ? resp.data.data : [];

          this.regencyOptions = data.map((regency) => ({
            label: regency.nama_kotakab || regency.nama_kabupaten || regency.name || regency.label,
            value: regency.kode_kotakab || regency.kode_kabupaten || regency.id || regency.code || regency.value,
          }));
        })
        .catch(() => {
          this.regencyOptions = [];
        })
        .finally(() => {
          this.isRegencyLoading = false;
        });
    },
    fetchDistrictOptions(kodeKotakabupaten) {
      this.isDistrictLoading = true;
      const params = kodeKotakabupaten ? { kode_kotakabupaten: kodeKotakabupaten } : {};

      return this.$http
        .get("/api/master/kecamatan", { params })
        .then((resp) => {
          const data = resp.data && resp.data.data ? resp.data.data : [];

          this.districtOptions = data.map((district) => ({
            label: district.nama_kecamatan || district.name || district.label,
            value: district.kode_kecamatan || district.id || district.code || district.value,
          }));
        })
        .catch(() => {
          this.districtOptions = [];
        })
        .finally(() => {
          this.isDistrictLoading = false;
        });
    },
    openDistrictDropdown() {
      if (!this.isDistrictLoading) this.showDistrictDropdown = true;
    },
    handleDistrictSearchInput() {
      this.selectedDistrict = null;
      this.radiusRows = [];
      this.markers = [];
      this.radiusCenter = null;
      this.mapFeatureLabels = [];
      this.isMapLoading = false;
      this.mapRequestToken += 1;
      this.clearGeoJsonLayer();
      this.clearCircleOverlays();
      this.openDistrictDropdown();
    },
    toggleDistrictDropdown() {
      if (this.isDistrictLoading) return;
      this.showDistrictDropdown = !this.showDistrictDropdown;
    },
    selectDistrict(district) {
      this.selectedDistrict = district.value;
      this.districtSearch = district.label;
      this.showDistrictDropdown = false;
      this.fetchRadiusDistribution(district.value);
      this.fetchMapDistribution(district.value);
      this.fetchDomainList(district.value);
    },
    getProvinceCode(val) {
      if (!val) return undefined;
      const match = this.provinceOptions.find((p) => p.label === val || p.value === val);
      return match ? match.value : val;
    },
    getRegencyCode(val) {
      if (!val) return undefined;
      const match = this.regencyOptions.find((r) => r.label === val || r.value === val);
      return match ? match.value : val;
    },
    getDistrictCode(val) {
      if (!val) return undefined;
      const match = this.districtOptions.find((d) => d.label === val || d.value === val);
      return match ? match.value : val;
    },
    fetchRadiusDistribution(district) {
      const requestToken = this.radiusRequestToken + 1;
      this.radiusRequestToken = requestToken;
      this.isRadiusLoading = true;
      this.radiusRows = [];

      const provinceCode = this.selectedProvinceCode || this.getProvinceCode(this.filter.province);
      const regencyCode = this.selectedRegencyCode || this.getRegencyCode(this.filter.regency);
      const districtCode = typeof district === "object" ? district.value : (this.getDistrictCode(district) || district);

      const params = {
        district: districtCode,
        domain: this.filter.domain && this.filter.domain !== "All Domains" ? this.filter.domain : undefined,
        province: provinceCode || undefined,
        region: regencyCode || undefined,
        // start_date: this.filter.start_date, // Future-proofing for datepicker
        // end_date: this.filter.end_date,     // Future-proofing for datepicker
      };

      this.$http
        .get("/api/domain-detection-domain-distribution/radius-distribution", { params })
        .then((resp) => {
          if (requestToken !== this.radiusRequestToken) return;

          const data = resp.data && resp.data.data ? resp.data.data : [];

          this.radiusRows = this.normalizeRadiusRows(data, districtCode);
        })
        .catch(() => {
          if (requestToken === this.radiusRequestToken) this.radiusRows = [];
        })
        .finally(() => {
          if (requestToken === this.radiusRequestToken) this.isRadiusLoading = false;
        });
    },
    fetchMapDistribution(district) {
      const requestToken = this.mapRequestToken + 1;
      this.mapRequestToken = requestToken;
      this.isMapLoading = true;
      this.markers = [];
      this.radiusCenter = null;
      this.mapFeatureLabels = [];
      this.clearGeoJsonLayer();
      this.clearCircleOverlays();

      const provinceCode = this.selectedProvinceCode || this.getProvinceCode(this.filter.province);
      const regencyCode = this.selectedRegencyCode || this.getRegencyCode(this.filter.regency);
      const districtCode = typeof district === "object" ? district.value : (this.getDistrictCode(district) || district);

      const params = {
        district: districtCode,
        domain: this.filter.domain && this.filter.domain !== "All Domains" ? this.filter.domain : undefined,
        province: provinceCode || undefined,
        region: regencyCode || undefined,
        // start_date: this.filter.start_date, // Future-proofing for datepicker
        // end_date: this.filter.end_date,     // Future-proofing for datepicker
      };

      this.$http
        .get("/api/domain-detection-domain-distribution/map", { params })
        .then((resp) => {
          if (requestToken !== this.mapRequestToken) return;

          const data = resp.data && resp.data.data ? resp.data.data : [];

          if (this.isRadiusDistributionResponse(data)) {
            this.applyRadiusDistributionMap(data, district, requestToken);
            return;
          }

          if (this.isFeatureCollection(data)) {
            this.setMapLegendChecksFromFeatures(data.features);
            this.loadFeatureCollectionLayer(data, requestToken)
              .catch(() => { })
              .finally(() => {
                this.$nextTick(this.fitDistributionMap);
              });
            return;
          }

          if (this.isBucketCenterRows(data)) {
            this.setMapLegendChecksFromRows(data, Boolean(data.length));
            this.markers = this.normalizeBucketCenterRows(data, district);
            this.renderBucketCircleOverlays(requestToken)
              .catch(() => { })
              .finally(() => {
                this.$nextTick(this.fitDistributionMap);
              });
            return;
          }

          this.markers = data
            .map((row) => ({
              id: row.id,
              lat: Number(row.lat),
              lng: Number(row.lng),
              district: row.district,
              region: row.region,
              province: row.province,
            }))
            .filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng));

          this.loadGeoJsonLayer(data, requestToken)
            .catch(() => { })
            .finally(() => {
              this.$nextTick(this.fitDistributionMap);
            });
        })
        .catch(() => {
          if (requestToken === this.mapRequestToken) {
            this.markers = [];
            this.radiusCenter = null;
            this.mapFeatureLabels = [];
            this.clearGeoJsonLayer();
            this.clearCircleOverlays();
          }
        })
        .finally(() => {
          if (requestToken === this.mapRequestToken) this.isMapLoading = false;
        });
    },
    fetchDomainList(district) {
      const requestToken = this.domainRequestToken + 1;
      this.domainRequestToken = requestToken;
      this.isDomainLoading = true;
      this.domainRows = [];

      const provinceCode = this.selectedProvinceCode || this.getProvinceCode(this.filter.province);
      const regencyCode = this.selectedRegencyCode || this.getRegencyCode(this.filter.regency);
      const districtCode = typeof district === "object" ? district.value : (this.getDistrictCode(district) || district);

      const params = {
        page: this.domainListCurrentPage,
        limit: this.domainListLimit,
        // start_date: this.filter.start_date, // Future-proofing for datepicker
        // end_date: this.filter.end_date,     // Future-proofing for datepicker
        domain: this.filter.domain && this.filter.domain !== "All Domains" ? this.filter.domain : undefined,
        province: provinceCode || undefined,
        region: regencyCode || undefined,
        district: districtCode,
        domain_status: this.filter.domain_status || undefined,
        search_domain: this.domainListSearch || undefined,
      };

      this.$http
        .get("/api/domain-detection-domain-distribution/domain-list", { params })
        .then((resp) => {
          if (requestToken !== this.domainRequestToken) return;

          const resData = resp.data && resp.data.data ? resp.data.data : (resp.data || {});
          const pagination = resData.pagination || resp.data.pagination || {};

          const rowsData = Array.isArray(resData) ? resData : (resData.data || resData.details || []);
          this.domainRows = this.normalizeDomainRows(rowsData);

          this.updateLatestScanDate(resp.data, rowsData);

          this.domainListCurrentPage = Number.parseInt(pagination.page || pagination.current_page) || this.domainListCurrentPage;
          this.domainListMaxPage = Number.parseInt(pagination.total_page || pagination.last_page) || 1;
          this.domainListTotal = Number.parseInt(pagination.total_data || pagination.total) || (Array.isArray(resData) ? resData.length : (resData.data ? resData.data.length : this.domainRows.length));
        })
        .catch(() => {
          if (requestToken === this.domainRequestToken) {
            this.domainRows = [];
            this.domainListTotal = 0;
          }
        })
        .finally(() => {
          if (requestToken === this.domainRequestToken) this.isDomainLoading = false;
        });
    },
    changeDomainListPage(direction) {
      if (direction === "prev" && this.domainListCurrentPage > 1) {
        this.domainListCurrentPage--;
        if (this.selectedDistrict) this.fetchDomainList(this.selectedDistrict);
      } else if (direction === "next" && this.domainListCurrentPage < this.domainListMaxPage) {
        this.domainListCurrentPage++;
        if (this.selectedDistrict) this.fetchDomainList(this.selectedDistrict);
      }
    },
    onDomainListLimitChange() {
      this.domainListCurrentPage = 1;
      if (this.selectedDistrict) this.fetchDomainList(this.selectedDistrict);
    },
    selectStatusOption(opt) {
      this.filter.domain_status = opt === "All Status" ? null : opt;
      this.openDropdowns.status = false;
      if (this.selectedDistrict) {
        this.fetchDomainList(this.selectedDistrict);
      }
    },
    formatRadiusDisplayLabel(value) {
      if (!value) return "-";
      const str = String(value).trim();
      const bucketKey = this.normalizeRadiusBucket(str);

      if (bucketKey === "0-500") return "0 m - 500 m";
      if (bucketKey === "500-1") return "> 500 m - 1 km";
      if (bucketKey === "1-2") return "> 1 km - 2 km";
      if (bucketKey === "2-5") return "> 2 km - 5 km";
      if (bucketKey === "5-plus") return "> 5 km - 10 km";

      if (/^(500|1|2|5)\s*(m|km)/i.test(str) && !str.startsWith(">")) {
        return `> ${str}`;
      }
      return str;
    },
    getRingNumber(bucketKey) {
      const mapping = {
        "0-500": 1,
        "500-1": 2,
        "1-2": 3,
        "2-5": 4,
        "5-plus": 5,
      };
      return mapping[bucketKey] || 1;
    },
    getRingLabel(bucketKey) {
      const mapping = {
        "0-500": "0 m - 500 m",
        "500-1": "> 500 m - 1 km",
        "1-2": "> 1 km - 2 km",
        "2-5": "> 2 km - 5 km",
        "5-plus": "> 5 km - 10 km",
      };
      return mapping[bucketKey] || "";
    },
    openRingModal(bucketKey) {
      const ringNumber = this.getRingNumber(bucketKey);
      const label = this.formatRadiusDisplayLabel(this.getRingLabel(bucketKey));
      const bucket = this.getLegendBucket(bucketKey);

      this.activeRingModal = {
        bucketKey,
        ringNumber,
        label,
        color: bucket ? bucket.color : "#1E3A8A",
        totalScans: 0,
        totalDomains: 0,
        search: "",
        sort: "desc",
        loading: true,
        list: [],
      };

      this.fetchRingDomains();
    },
    closeRingModal() {
      this.activeRingModal = null;
    },
    handleRingSortChange() {
      if (!this.activeRingModal) return;
      this.fetchRingDomains();
    },
    toggleRingSort() {
      if (!this.activeRingModal) return;
      this.activeRingModal.sort = this.activeRingModal.sort === "asc" ? "desc" : "asc";
      this.fetchRingDomains();
    },
    handleRingSearchInput() {
      if (!this.activeRingModal) return;
      if (this.ringSearchTimer) clearTimeout(this.ringSearchTimer);
      this.ringSearchTimer = setTimeout(() => {
        this.fetchRingDomains();
      }, 300);
    },
    fetchRingDomains() {
      if (!this.activeRingModal || !this.selectedDistrict) return;

      const modal = this.activeRingModal;
      modal.loading = true;

      const provinceCode = this.selectedProvinceCode || this.getProvinceCode(this.filter.province);
      const regencyCode = this.selectedRegencyCode || this.getRegencyCode(this.filter.regency);
      const districtCode = typeof this.selectedDistrict === "object" ? this.selectedDistrict.value : (this.getDistrictCode(this.selectedDistrict) || this.selectedDistrict);

      const params = {
        ring: modal.ringNumber,
        // start_date: this.filter.start_date, // Future-proofing for datepicker
        // end_date: this.filter.end_date,     // Future-proofing for datepicker
        district: districtCode,
        domain: this.filter.domain && this.filter.domain !== "All Domains" ? this.filter.domain : undefined,
        province: provinceCode || undefined,
        region: regencyCode || undefined,
        sort: modal.sort,
        search: modal.search || undefined,
      };

      this.$http
        .get("/api/domain-detection-domain-distribution/ring-domains", { params })
        .then((resp) => {
          if (!this.activeRingModal || this.activeRingModal.bucketKey !== modal.bucketKey) return;

          const resObj = resp.data && resp.data.data ? resp.data.data : (resp.data || {});
          let domainsList = [];

          if (Array.isArray(resObj)) {
            domainsList = resObj;
          } else if (Array.isArray(resObj.data)) {
            domainsList = resObj.data;
          } else if (Array.isArray(resObj.domains)) {
            domainsList = resObj.domains;
          } else if (Array.isArray(resObj.list)) {
            domainsList = resObj.list;
          } else if (Array.isArray(resObj.details)) {
            domainsList = resObj.details;
          }

          const rawTotalScans = resObj.total_scan != null ? resObj.total_scan
            : resObj.totalScans != null ? resObj.totalScans
              : modal.totalScans;

          const rawTotalDomains = resObj.total_domain != null ? resObj.total_domain
            : resObj.totalDomains != null ? resObj.totalDomains
              : domainsList.length;

          if (resObj.radius_label) {
            modal.label = this.formatRadiusDisplayLabel(resObj.radius_label);
          }

          modal.totalScans = rawTotalScans;
          modal.totalDomains = rawTotalDomains;

          modal.list = domainsList.map((item, idx) => {
            const rawDomain = item.domain_url || item.domain || item.name || "-";
            const cleanDomain = String(rawDomain).replace(/^https?:\/\//i, "").replace(/\/$/, "");

            return {
              id: item.domain_id || item.id || idx,
              domain: cleanDomain,
              scans: this.formatNumber(item.total_scan != null ? item.total_scan : (item.scans != null ? item.scans : item.scan || 0)),
              status: item.domain_status || item.status || "-",
            };
          });
        })
        .catch(() => {
          if (this.activeRingModal && this.activeRingModal.bucketKey === modal.bucketKey) {
            modal.list = [];
          }
        })
        .finally(() => {
          if (this.activeRingModal && this.activeRingModal.bucketKey === modal.bucketKey) {
            modal.loading = false;
          }
        });
    },
    isWatchlisted(status) {
      if (!status) return false;
      const statusStr = String(status).toLowerCase().trim();
      return statusStr.includes("watchlisted") || statusStr.includes("watchlist");
    },
    handleWatchlist(row) {
      const domainId = row.id || row.domain_id;
      if (!domainId) return;

      this.$http
        .post("/api/domain-detection-domain-distribution/update-watchlist", {
          domain_id: domainId,
          watchlist: true,
        })
        .then(() => {
          row.status = "Watchlisted";
          if (this.selectedDistrict) {
            this.fetchDomainList(this.selectedDistrict);
          }
          if (this.activeRingModal) {
            this.fetchRingDomains();
          }
          this.$emit("watchlist", row);
        })
        .catch((err) => {
          if (typeof this.errorPrint === "function") {
            this.errorPrint(err);
          }
        });
    },
    handleDetail(row) {
      const domainId = row.id || row.domain_id;
      if (domainId) {
        this.$router.push({ name: 'ReviewDomainDetail', params: { domain_id: domainId } }).catch(() => { });
      } else {
        this.$emit("detail", row);
      }
    },
    getStatusBadgeClass(status) {
      if (!status || status === "-") return "";
      const statusStr = String(status).toLowerCase().trim();
      if (statusStr.includes("under review") || statusStr.includes("under_review") || statusStr.includes("review")) {
        return "status-badge status-badge-under-review";
      }
      if (statusStr.includes("watchlist") || statusStr.includes("watchlisted")) {
        return "status-badge status-badge-watchlisted";
      }
      return "status-badge";
    },
    getRiskBadgeClass(riskScore) {
      if (!riskScore || riskScore === "-") return "";
      const scoreStr = String(riskScore).toLowerCase().trim();
      if (scoreStr.includes("high") || scoreStr === "tinggi") {
        return "risk-badge risk-badge-high";
      }
      if (scoreStr.includes("medium") || scoreStr.includes("med") || scoreStr === "sedang") {
        return "risk-badge risk-badge-medium";
      }
      if (scoreStr.includes("low") || scoreStr === "rendah") {
        return "risk-badge risk-badge-low";
      }
      const num = Number(scoreStr);
      if (Number.isFinite(num)) {
        if (num >= 70) return "risk-badge risk-badge-high";
        if (num >= 40) return "risk-badge risk-badge-medium";
        return "risk-badge risk-badge-low";
      }
      return "risk-badge";
    },
    formatFirstScanDate(val) {
      if (!val || val === "-") return "-";

      const valStr = String(val).trim();
      if (/^\d{4}-\d{2}-\d{2}\s*\|\s*\d{2}:\d{2}:\d{2}$/.test(valStr)) {
        return valStr;
      }

      const date = new Date(val);
      if (isNaN(date.getTime())) return valStr;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${year}-${month}-${day} | ${hours}:${minutes}:${seconds}`;
    },
    updateLatestScanDate(respData, rowsData) {
      let latestTime = null;
      let latestRaw = null;

      const resData = respData && respData.data ? respData.data : (respData || {});
      const topLevelCandidate = (respData && (respData.latest_update || respData.first_scan)) ||
        (resData && (resData.latest_update || resData.first_scan));
      if (topLevelCandidate) {
        const cleanTop = typeof topLevelCandidate === "string" ? topLevelCandidate.replace(" | ", " ") : topLevelCandidate;
        const d = new Date(cleanTop);
        if (!isNaN(d.getTime())) {
          latestTime = d.getTime();
          latestRaw = topLevelCandidate;
        }
      }

      if (Array.isArray(rowsData)) {
        rowsData.forEach((row) => {
          const raw = row.first_scan || row.firstScan || row.created_at || row.date;
          if (raw) {
            const cleanRaw = typeof raw === "string" ? raw.replace(" | ", " ") : raw;
            const d = new Date(cleanRaw);
            if (!isNaN(d.getTime())) {
              const time = d.getTime();
              if (latestTime === null || time > latestTime) {
                latestTime = time;
                latestRaw = raw;
              }
            }
          }
        });
      }

      if (latestRaw) {
        this.latestUpdate = this.formatFirstScanDate(latestRaw);
      } else if (!this.latestUpdate) {
        this.latestUpdate = "-";
      }
    },
    async fetchLatestUpdate() {
      try {
        const resp = await this.$http.get("/api/domain-detection-domain-distribution/domain-list", {
          params: { page: 1, limit: 100 }
        });
        const resData = resp.data && resp.data.data ? resp.data.data : (resp.data || {});
        const rowsData = Array.isArray(resData) ? resData : (resData.data || resData.details || []);

        this.updateLatestScanDate(resp.data, rowsData);
      } catch (err) {
        if (!this.latestUpdate) this.latestUpdate = "-";
      }
    },
    normalizeDomainRows(data) {
      let rows = data;
      if (!Array.isArray(rows)) {
        if (rows && Array.isArray(rows.data)) rows = rows.data;
        else if (rows && Array.isArray(rows.details)) rows = rows.details;
        else rows = [];
      }

      return rows.map((row, index) => {
        const total = row.total_scan != null ? row.total_scan
          : row.totalScans != null ? row.totalScans
            : row.total_findings != null ? row.total_findings
              : row.total_count != null ? row.total_count
                : row.count != null ? row.count : 0;

        const rawFirstScan = row.first_scan || row.firstScan || row.created_at || row.date;
        const firstScan = this.formatFirstScanDate(rawFirstScan);
        const riskScore = row.risk_score != null ? row.risk_score
          : row.riskScore != null ? row.riskScore
            : row.score != null ? row.score : "-";

        return {
          id: row.id || row.domain_id || index,
          domain: row.domain || row.domain_url || row.domain_name || row.name || "-",
          totalScans: this.formatNumber(total),
          firstScan,
          riskScore,
          status: row.domain_status || row.status || "-",
        };
      });
    },
    getDistributionMap() {
      if (!this.$refs.distributionMap || !this.$refs.distributionMap.$mapPromise) {
        return Promise.resolve(null);
      }

      return this.$refs.distributionMap.$mapPromise;
    },
    async syncDistributionMapOptions() {
      const map = await this.getDistributionMap();
      if (!map) return;

      map.setOptions(this.distributionMapOptions);
    },
    isFeatureCollection(data) {
      return data && data.type === "FeatureCollection" && Array.isArray(data.features);
    },
    isRadiusDistributionResponse(data) {
      return Boolean(
        data &&
        !Array.isArray(data) &&
        (data.center_point || Array.isArray(data.radius_summary) || Array.isArray(data.details))
      );
    },
    isBucketCenterRows(data) {
      return (
        Array.isArray(data) &&
        data.some((row) => row && row.center_lat != null && row.center_lng != null && row.radius_bucket)
      );
    },
    normalizeRadiusRows(data, district) {
      const rows = this.getRadiusSummaryRows(data);

      return rows
        .slice()
        .sort((a, b) => Number(a.radius_order) - Number(b.radius_order))
        .map((row) => ({
          district: row.kode_kecamatan || row.district_code || district,
          radius: this.formatRadiusDisplayLabel(row.radius_bucket || row.radius),
          totalScans: this.formatNumber(row.total_scan),
          percentage: this.formatPercentage(row.percentage),
        }));
    },
    getRadiusSummaryRows(data) {
      if (Array.isArray(data)) return data;
      if (!data) return [];
      if (Array.isArray(data.radius_summary)) return data.radius_summary;
      if (Array.isArray(data.details)) return data.details;
      return [];
    },
    applyRadiusDistributionMap(data, district, requestToken) {
      this.radiusCenter = this.normalizeCenterPoint(data.center_point);
      this.setMapLegendChecksFromRows(this.getRadiusSummaryRows(data), Boolean(this.radiusCenter));
      this.markers = this.normalizeBucketCenterRows(this.getRadiusSummaryRows(data), district, this.radiusCenter);
      this.mapFeatureLabels = [];

      if (this.radiusRows.length === 0) {
        this.radiusRows = this.normalizeRadiusRows(data, district);
      }

      this.renderBucketCircleOverlays(requestToken)
        .catch(() => { })
        .finally(() => {
          this.$nextTick(this.fitDistributionMap);
        });
    },
    normalizeCenterPoint(centerPoint) {
      if (!centerPoint) return null;

      const lat = Number(centerPoint.lat);
      const lng = Number(centerPoint.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

      return { lat, lng };
    },
    normalizeBucketCenterRows(data, district, centerPoint) {
      return data
        .slice()
        .sort((a, b) => Number(a.radius_order) - Number(b.radius_order))
        .map((row, index) => {
          const bucketKey = this.normalizeRadiusBucket(row.radius_bucket);
          const lat = Number(row.center_lat);
          const lng = Number(row.center_lng);
          const radiusLabelPosition =
            centerPoint && bucketKey ? this.getRadiusLabelPosition(bucketKey, centerPoint) : null;
          let markerLat = Number.isFinite(lat) ? lat : centerPoint && centerPoint.lat;
          let markerLng = Number.isFinite(lng) ? lng : centerPoint && centerPoint.lng;

          if (radiusLabelPosition) {
            markerLat = radiusLabelPosition.lat;
            markerLng = radiusLabelPosition.lng;
          }

          return {
            id: `${row.kode_kecamatan || district}-${bucketKey || index}`,
            lat: markerLat,
            lng: markerLng,
            district: row.kode_kecamatan || district,
            districtName: row.nama_kecamatan,
            radiusBucket: row.radius_bucket,
            bucketKey: bucketKey || "5-plus",
            totalScans: Number(row.total_scan) || 0,
            location: Number(row.location) || 0,
            percentage: row.percentage,
          };
        })
        .filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng));
    },
    formatNumber(value) {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) return value || 0;

      return numberValue.toLocaleString("en-US");
    },
    formatPercentage(value) {
      if (value == null || value === "") return "0%";

      const text = String(value);
      return text.endsWith("%") ? text : `${text}%`;
    },
    async renderBucketCircleOverlays(requestToken) {
      if (!window.google) return;

      const map = await this.getDistributionMap();
      if (!map || requestToken !== this.mapRequestToken) return;

      this.clearCircleOverlays();
      const circleSources = this.radiusCenter
        ? this.getVisibleRadiusBuckets()
          .sort((a, b) => this.getBucketRadiusMeters(b) - this.getBucketRadiusMeters(a))
          .map((bucketKey) => ({
            bucketKey,
            lat: this.radiusCenter.lat,
            lng: this.radiusCenter.lng,
          }))
        : this.markers
          .slice()
          .sort((a, b) => this.getBucketRadiusMeters(b.bucketKey) - this.getBucketRadiusMeters(a.bucketKey));

      circleSources.forEach((source) => {
        const bucket = this.getLegendBucket(source.bucketKey);
        const center = { lat: source.lat, lng: source.lng };
        const { inner, outer } = this.getBucketInnerOuterRadiusMeters(source.bucketKey);

        const outerPath = this.getCirclePoints(center, outer);
        const paths = [outerPath];

        if (inner > 0) {
          const innerPath = this.getCirclePoints(center, inner).reverse();
          paths.push(innerPath);
        }

        const overlay = new window.google.maps.Polygon({
          clickable: true,
          fillColor: bucket.color,
          fillOpacity: this.getBucketFillOpacity(source.bucketKey),
          map,
          paths,
          strokeColor: bucket.color,
          strokeOpacity: this.getBucketStrokeOpacity(source.bucketKey),
          strokeWeight: this.getBucketStrokeWeight(source.bucketKey),
        });

        overlay.bucketKey = source.bucketKey;
        window.google.maps.event.addListener(overlay, "click", () => {
          this.openRingModal(source.bucketKey);
        });
        this.circleOverlays.push(overlay);
      });
      this.updateCircleOverlayVisibility();
    },
    async loadBucketCenterGeoJsonLayer(requestToken) {
      if (!window.google) return;

      const map = await this.getDistributionMap();
      if (!map || requestToken !== this.mapRequestToken) return;

      const provinceGeoJson = await this.getProvinceGeoJson(requestToken);
      if (!provinceGeoJson || requestToken !== this.mapRequestToken) {
        await this.renderBucketCircleOverlays(requestToken);
        return;
      }

      const polygonFeatures = [];
      const fallbackMarkers = [];
      const polygonGroups = {};

      for (const marker of this.markers) {
        const provinceFeature = this.findContainingFeature(provinceGeoJson.features, marker);
        const provinceName = provinceFeature && provinceFeature.properties && provinceFeature.properties.provinsi;

        if (!provinceName) {
          fallbackMarkers.push(marker);
          continue;
        }

        const detailGeoJson = await this.getDetailProvinceGeoJson(provinceName, requestToken);
        if (!detailGeoJson || requestToken !== this.mapRequestToken) return;

        const feature = this.findContainingFeature(detailGeoJson.features, marker);
        if (!feature) {
          fallbackMarkers.push(marker);
          continue;
        }

        const polygonCoordinates = this.getContainingPolygonCoordinates(feature.geometry, marker);
        if (!polygonCoordinates) {
          fallbackMarkers.push(marker);
          continue;
        }

        const featureKey = this.getMapFeatureKey(feature, polygonCoordinates);
        if (!polygonGroups[featureKey]) {
          polygonGroups[featureKey] = [];
        }
        polygonGroups[featureKey].push({
          marker,
          feature,
          polygonCoordinates,
        });
      }

      Object.values(polygonGroups).forEach((group) => {
        group.forEach((item) => {
          const cellCoordinates =
            group.length > 1
              ? this.createCellCoordinates(item.polygonCoordinates, item.marker, group.map((entry) => entry.marker))
              : item.polygonCoordinates;

          if (!cellCoordinates) {
            fallbackMarkers.push(item.marker);
            return;
          }

          polygonFeatures.push(this.createBucketFeature(item.feature, item.marker, cellCoordinates));
        });
      });

      if (polygonFeatures.length > 0) {
        const layer = new window.google.maps.Data({ map });
        layer.addGeoJson({
          type: "FeatureCollection",
          features: polygonFeatures,
        });
        this.geoJsonLayer = layer;
        this.mapFeatureLabels = polygonFeatures
          .map((feature, index) => this.createFeatureLabel(feature, index))
          .filter(Boolean);
        this.styleGeoJsonLayer();
      }

      if (fallbackMarkers.length > 0) {
        this.mapFeatureLabels = this.mapFeatureLabels.concat(
          fallbackMarkers.map((marker) => this.createMarkerLabel(marker))
        );
        const originalMarkers = this.markers;
        this.markers = fallbackMarkers;
        await this.renderBucketCircleOverlays(requestToken);
        this.markers = originalMarkers;
      }
    },
    async getProvinceGeoJson(requestToken) {
      if (this.provinceGeoJson) return this.provinceGeoJson;

      const response = await fetch("/geojson/indonesia.json");
      if (!response.ok || requestToken !== this.mapRequestToken) return null;

      this.provinceGeoJson = await response.json();
      return this.provinceGeoJson;
    },
    async getDetailProvinceGeoJson(provinceName, requestToken) {
      const fileName = this.getProvinceGeoJsonFileName(provinceName);
      if (this.detailGeoJsonCache[fileName]) return this.detailGeoJsonCache[fileName];

      const response = await fetch(`/geojson/detail-provinsi/${encodeURIComponent(fileName)}.geojson`);
      if (!response.ok || requestToken !== this.mapRequestToken) return null;

      const geoJson = await response.json();
      this.detailGeoJsonCache[fileName] = geoJson;
      return geoJson;
    },
    createBucketFeature(sourceFeature, marker, polygonCoordinates) {
      const bucket = this.getLegendBucket(marker.bucketKey);

      return {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: polygonCoordinates,
        },
        properties: {
          ...(sourceFeature.properties || {}),
          _bucketKey: marker.bucketKey,
          _fillColor: bucket.color,
          _featureId: marker.id,
          _labelLat: marker.lat,
          _labelLng: marker.lng,
          total_findings: marker.totalScans,
        },
      };
    },
    async loadFeatureCollectionLayer(featureCollection, requestToken) {
      if (!window.google) return;

      const map = await this.getDistributionMap();
      if (!map || requestToken !== this.mapRequestToken) return;

      const featureCenters = featureCollection.features.map((feature) =>
        this.getGeoJsonGeometryCenter(feature.geometry)
      );
      const selectedFeatureIndex = this.getSelectedFeatureIndex(featureCollection.features);
      const selectedCenter = featureCenters[selectedFeatureIndex] || featureCenters.find(Boolean);

      const normalizedFeatureCollection = {
        ...featureCollection,
        features: featureCollection.features.map((feature, index) => {
          const properties = feature.properties || {};
          const center = featureCenters[index];
          const bucket = this.getFeatureBucket(properties, center, selectedCenter, index === selectedFeatureIndex);

          return {
            ...feature,
            properties: {
              ...properties,
              _bucketKey: bucket.key,
              _fillColor: properties.fill_color || properties.color || bucket.color,
              _featureId: properties.district_code || properties.id || `feature-${index}`,
            },
          };
        }),
      };

      const layer = new window.google.maps.Data({ map });
      layer.addGeoJson(normalizedFeatureCollection);
      this.geoJsonLayer = layer;
      this.mapFeatureLabels = normalizedFeatureCollection.features
        .map((feature, index) => this.createFeatureLabel(feature, index))
        .filter(Boolean);
      this.styleGeoJsonLayer();
    },
    getSelectedFeatureIndex(features) {
      const selectedCode = this.normalizeFeatureCode(this.selectedDistrict);
      const index = features.findIndex((feature) => {
        const properties = feature.properties || {};
        const code = properties.district_code || properties.kode_kecamatan || properties.id;

        return this.normalizeFeatureCode(code) === selectedCode;
      });

      return index >= 0 ? index : 0;
    },
    normalizeFeatureCode(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/^id/, "")
        .replace(/[^a-z0-9]/g, "");
    },
    getFeatureBucket(properties, center, selectedCenter, isSelectedFeature) {
      if (isSelectedFeature) return this.getLegendBucket("location");

      const explicitBucket = this.normalizeRadiusBucket(
        properties.radius_bucket || properties.radius || properties.bucket || properties.distance_bucket
      );
      if (explicitBucket) return this.getLegendBucket(explicitBucket);

      const distanceKm = Number(properties.distance_km || properties.distance);
      if (Number.isFinite(distanceKm)) return this.getRadiusBucketByDistance(distanceKm);

      if (center && selectedCenter) {
        return this.getRadiusBucketByDistance(this.getDistanceInKm(center, selectedCenter));
      }

      return this.getLegendBucket("5-plus");
    },
    normalizeRadiusBucket(value) {
      const normalized = String(value || "").toLowerCase().replace(/\s+/g, " ").trim();
      const compact = normalized.replace(/\s+/g, "");

      if (!normalized) return "";
      if (normalized.includes("location")) return "location";
      if (
        compact.includes("0-500") ||
        compact.includes("0m-500") ||
        compact.includes("0-500m") ||
        compact.includes("0m-500m")
      ) {
        return "0-500";
      }
      if (
        compact.includes("500m-1km") ||
        compact.includes("500-1km") ||
        compact.includes("500m-1") ||
        compact.includes("500-1")
      ) {
        return "500-1";
      }
      if (
        compact.includes("1km-2km") ||
        compact.includes("1-2km") ||
        compact.includes("1-2")
      ) {
        return "1-2";
      }
      if (
        compact.includes("2km-5km") ||
        compact.includes("2-5km") ||
        compact.includes("2-5")
      ) {
        return "2-5";
      }
      if (
        compact.includes("5km-10km") ||
        compact.includes("5-10") ||
        compact.includes("5km") ||
        compact.includes("5-plus") ||
        compact.includes("10km") ||
        normalized.includes("plus") ||
        normalized.includes("> 5 km") ||
        normalized.includes(">5km")
      ) {
        return "5-plus";
      }
      return "";
    },
    setMapLegendChecksFromRows(rows, includeLocation) {
      const activeKeys = (Array.isArray(rows) ? rows : [])
        .filter((row) => this.hasActiveRadiusValue(row))
        .map((row) => this.normalizeRadiusBucket(row.radius_bucket || row.radius || row.bucket))
        .filter((key) => key && key !== "location");

      this.setMapLegendChecks(activeKeys, includeLocation);
    },
    setMapLegendChecksFromFeatures(features) {
      const activeKeys = (Array.isArray(features) ? features : [])
        .filter((feature) => this.hasActiveRadiusValue(feature && feature.properties))
        .map((feature) => {
          const properties = feature.properties || {};
          return this.normalizeRadiusBucket(
            properties.radius_bucket || properties.radius || properties.bucket || properties.distance_bucket
          );
        })
        .filter((key) => key && key !== "location");

      this.setMapLegendChecks(activeKeys, true);
    },
    setMapLegendChecks(activeKeys, includeLocation) {
      const activeKeySet = new Set(activeKeys);

      this.mapLegendItems.forEach((item) => {
        item.checked = item.key === "location" ? Boolean(includeLocation) : activeKeySet.has(item.key);
      });

      this.$nextTick(() => {
        this.styleGeoJsonLayer();
        this.updateCircleOverlayVisibility();
      });
    },
    hasActiveRadiusValue(row) {
      if (!row) return false;

      const values = [row.total_scan, row.totalScans, row.location, row.total_findings]
        .filter((value) => value !== undefined && value !== null && value !== "")
        .map((value) => Number(String(value).replace(/,/g, "")))
        .filter((value) => Number.isFinite(value));

      return values.length === 0 || values.some((value) => value > 0);
    },
    async loadGeoJsonLayer(mapData, requestToken) {
      const firstRow = mapData.find((row) => row.province);
      if (!firstRow || !window.google) return;

      const map = await this.getDistributionMap();
      if (!map || requestToken !== this.mapRequestToken) return;

      const fileName = this.getProvinceGeoJsonFileName(firstRow.province);
      const response = await fetch(`/geojson/detail-provinsi/${encodeURIComponent(fileName)}.geojson`);
      if (!response.ok || requestToken !== this.mapRequestToken) return;

      const geoJson = await response.json();
      if (requestToken !== this.mapRequestToken) return;

      const targetRegion = this.normalizeRegionName(firstRow.region);
      const layer = new window.google.maps.Data({ map });

      layer.addGeoJson(geoJson);
      layer.targetRegion = targetRegion;
      this.geoJsonLayer = layer;
      this.styleGeoJsonLayer();
    },
    clearGeoJsonLayer() {
      if (this.geoJsonLayer) {
        this.geoJsonLayer.setMap(null);
        this.geoJsonLayer = null;
      }
      this.mapFeatureLabels = [];
    },
    clearCircleOverlays() {
      this.circleOverlays.forEach((circle) => circle.setMap(null));
      this.circleOverlays = [];
    },
    toggleMapLegend(key) {
      const item = this.mapLegendItems.find((legendItem) => legendItem.key === key);
      if (!item) return;

      item.checked = !item.checked;
      this.styleGeoJsonLayer();
      this.updateCircleOverlayVisibility();
    },
    toggleLegendCollapse() {
      this.isMapLegendCollapsed = !this.isMapLegendCollapsed;
    },
    updateCircleOverlayVisibility() {
      this.circleOverlays.forEach((circle) => {
        const visible = this.isMapLegendChecked(circle.bucketKey);
        circle.setOptions({
          fillOpacity: visible ? this.getBucketFillOpacity(circle.bucketKey) : 0,
          strokeOpacity: visible ? this.getBucketStrokeOpacity(circle.bucketKey) : 0,
          strokeWeight: visible ? this.getBucketStrokeWeight(circle.bucketKey) : 0,
        });
      });
    },
    isMapLegendChecked(key) {
      const item = this.mapLegendItems.find((legendItem) => legendItem.key === key);
      return item ? item.checked : true;
    },
    getMarkerCenter(markers) {
      const totals = markers.reduce(
        (acc, marker) => {
          acc.lat += marker.lat;
          acc.lng += marker.lng;
          return acc;
        },
        { lat: 0, lng: 0 }
      );

      return {
        lat: totals.lat / markers.length,
        lng: totals.lng / markers.length,
      };
    },
    createMarkerLabel(marker) {
      const text = String(marker.totalScans || marker.location || 0);

      return {
        id: marker.id,
        bucketKey: marker.bucketKey,
        lat: marker.lat,
        lng: marker.lng,
        markerLabel: {
          text,
          color: this.getMarkerLabelColor(marker.bucketKey),
          fontSize: text.length > 2 ? "12px" : "14px",
          fontWeight: "700",
        },
      };
    },
    getRadiusBucket(marker, center) {
      return this.getRadiusBucketByDistance(this.getDistanceInKm(marker, center));
    },
    getRadiusBucketByDistance(distance) {

      if (distance <= 0.5) return this.getLegendBucket("0-500");
      if (distance <= 1) return this.getLegendBucket("500-1");
      if (distance <= 2) return this.getLegendBucket("1-2");
      if (distance <= 5) return this.getLegendBucket("2-5");
      return this.getLegendBucket("5-plus");
    },
    getLegendBucket(key) {
      const item = this.mapLegendItems.find((legendItem) => legendItem.key === key);
      return {
        key,
        color: item ? item.color : "#666",
      };
    },
    getMarkerLabelColor() {
      return "#111111";
    },
    getVisibleRadiusBuckets() {
      const buckets = this.markers
        .map((marker) => marker.bucketKey)
        .filter(Boolean);

      return [...new Set(buckets)];
    },
    getRadiusLabelPosition(bucketKey, centerPoint) {
      const bandMidpointsKm = {
        "0-500": 0.25,
        "500-1": 0.75,
        "1-2": 1.5,
        "2-5": 3.5,
        "5-plus": 7.5,
      };
      const distanceKm = bandMidpointsKm[bucketKey] || 1;
      const latOffset = distanceKm / 110.574;

      return {
        lat: centerPoint.lat + latOffset,
        lng: centerPoint.lng,
      };
    },
    getCirclePoints(center, radiusMeters, numPoints = 72) {
      const points = [];
      const lat = center.lat;
      const lng = center.lng;
      const earthRadius = 6371000;

      const dLat = radiusMeters / earthRadius;
      const dLng = radiusMeters / (earthRadius * Math.cos((lat * Math.PI) / 180));

      for (let i = 0; i < numPoints; i++) {
        const theta = (i * 2 * Math.PI) / numPoints;
        const pointLat = lat + (dLat * Math.sin(theta) * 180) / Math.PI;
        const pointLng = lng + (dLng * Math.cos(theta) * 180) / Math.PI;
        points.push({ lat: pointLat, lng: pointLng });
      }
      return points;
    },
    getBucketInnerOuterRadiusMeters(key) {
      const ranges = {
        "0-500": { inner: 0, outer: 500 },
        "500-1": { inner: 500, outer: 1000 },
        "1-2": { inner: 1000, outer: 2000 },
        "2-5": { inner: 2000, outer: 5000 },
        "5-plus": { inner: 5000, outer: 10000 },
      };
      return ranges[key] || { inner: 0, outer: 1000 };
    },
    getBucketFillOpacity() {
      return 0.38;
    },
    getBucketStrokeOpacity() {
      return 0.85;
    },
    getBucketStrokeWeight() {
      return 1.2;
    },
    getBucketGridSize(key) {
      const gridSizes = {
        "0-500": 0.002,
        "500-1": 0.0025,
        "1-2": 0.003,
        "2-5": 0.004,
        "5-plus": 0.005,
      };

      return gridSizes[key] || 0.003;
    },
    getBucketRadiusMeters(key) {
      const radiusMeters = {
        "0-500": 500,
        "500-1": 1000,
        "1-2": 2000,
        "2-5": 5000,
        "5-plus": 10000,
      };

      return radiusMeters[key] || 1000;
    },
    getDistanceInKm(marker, center) {
      const earthRadiusKm = 6371;
      const latDelta = this.toRadians(marker.lat - center.lat);
      const lngDelta = this.toRadians(marker.lng - center.lng);
      const lat1 = this.toRadians(center.lat);
      const lat2 = this.toRadians(marker.lat);
      const a =
        Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
        Math.sin(lngDelta / 2) *
        Math.sin(lngDelta / 2) *
        Math.cos(lat1) *
        Math.cos(lat2);

      return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },
    toRadians(value) {
      return (value * Math.PI) / 180;
    },
    styleGeoJsonLayer() {
      if (!this.geoJsonLayer) return;

      if (!this.geoJsonLayer._clickAdded) {
        this.geoJsonLayer._clickAdded = true;
        this.geoJsonLayer.addListener("click", (event) => {
          const bucketKey = event.feature.getProperty("_bucketKey");
          if (bucketKey && bucketKey !== "location") {
            this.openRingModal(bucketKey);
          }
        });
      }

      this.geoJsonLayer.setStyle((feature) => {
        const bucketKey = feature.getProperty("_bucketKey");
        const fillColor = feature.getProperty("_fillColor") || this.getLegendBucket(bucketKey).color;
        const isVisible = this.isMapLegendChecked(bucketKey);
        const isClickable = Boolean(bucketKey && bucketKey !== "location");

        if (bucketKey) {
          return {
            clickable: isClickable,
            fillColor,
            fillOpacity: isVisible ? 0.48 : 0,
            strokeColor: fillColor,
            strokeOpacity: isVisible ? 0.95 : 0,
            strokeWeight: isVisible ? 1.2 : 0,
          };
        }

        const featureRegion = this.normalizeRegionName(feature.getProperty("kabkot"));
        const isSelectedRegion = featureRegion && featureRegion === this.geoJsonLayer.targetRegion;
        const isVisibleLegacy = this.isGeoJsonVisible;

        return {
          clickable: false,
          fillColor: "#CD2E2C",
          fillOpacity: isVisibleLegacy ? (isSelectedRegion ? 0.24 : 0.12) : 0,
          strokeColor: "#CD2E2C",
          strokeOpacity: isVisibleLegacy ? 0.85 : 0,
          strokeWeight: isSelectedRegion ? 2 : 1,
        };
      });
    },
    createFeatureLabel(feature, index) {
      if (!feature || !feature.geometry) return null;

      const properties = feature.properties || {};
      const totalFindings = Number(properties.total_findings);
      if (!Number.isFinite(totalFindings)) return null;

      const center =
        Number.isFinite(Number(properties._labelLat)) && Number.isFinite(Number(properties._labelLng))
          ? {
            lat: Number(properties._labelLat),
            lng: Number(properties._labelLng),
          }
          : this.getGeoJsonGeometryCenter(feature.geometry);
      if (!center) return null;

      const bucketKey = properties._bucketKey || "location";
      const text = String(totalFindings);

      return {
        id: properties._featureId || `feature-label-${index}`,
        bucketKey,
        lat: center.lat,
        lng: center.lng,
        markerLabel: {
          text,
          color: this.getMarkerLabelColor(bucketKey),
          fontSize: text.length > 2 ? "12px" : "14px",
          fontWeight: "700",
        },
      };
    },
    getGeoJsonGeometryCenter(geometry) {
      if (!geometry || !Array.isArray(geometry.coordinates)) return null;

      const bounds = {
        minLat: Infinity,
        maxLat: -Infinity,
        minLng: Infinity,
        maxLng: -Infinity,
      };

      this.extendPlainBoundsFromCoordinates(geometry.coordinates, bounds);
      if (!Number.isFinite(bounds.minLat) || !Number.isFinite(bounds.minLng)) return null;

      return {
        lat: (bounds.minLat + bounds.maxLat) / 2,
        lng: (bounds.minLng + bounds.maxLng) / 2,
      };
    },
    extendPlainBoundsFromCoordinates(coordinates, bounds) {
      if (!Array.isArray(coordinates)) return;

      if (
        coordinates.length >= 2 &&
        typeof coordinates[0] === "number" &&
        typeof coordinates[1] === "number"
      ) {
        const lng = coordinates[0];
        const lat = coordinates[1];

        bounds.minLat = Math.min(bounds.minLat, lat);
        bounds.maxLat = Math.max(bounds.maxLat, lat);
        bounds.minLng = Math.min(bounds.minLng, lng);
        bounds.maxLng = Math.max(bounds.maxLng, lng);
        return;
      }

      coordinates.forEach((item) => this.extendPlainBoundsFromCoordinates(item, bounds));
    },
    findContainingFeature(features, point) {
      if (!Array.isArray(features)) return null;

      return (
        features.find((feature) => this.isPointInGeoJsonGeometry(point, feature.geometry)) || null
      );
    },
    isPointInGeoJsonGeometry(point, geometry) {
      if (!geometry || !Array.isArray(geometry.coordinates)) return false;

      if (geometry.type === "Polygon") {
        return this.isPointInGeoJsonPolygon(point, geometry.coordinates);
      }

      if (geometry.type === "MultiPolygon") {
        return geometry.coordinates.some((polygon) => this.isPointInGeoJsonPolygon(point, polygon));
      }

      return false;
    },
    isPointInGeoJsonPolygon(point, polygon) {
      if (!Array.isArray(polygon) || polygon.length === 0) return false;
      if (!this.isPointInGeoJsonRing(point, polygon[0])) return false;

      return !polygon
        .slice(1)
        .some((hole) => this.isPointInGeoJsonRing(point, hole));
    },
    isPointInGeoJsonRing(point, ring) {
      if (!Array.isArray(ring) || ring.length < 3) return false;

      const x = point.lng;
      const y = point.lat;
      let inside = false;

      for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const xi = ring[i][0];
        const yi = ring[i][1];
        const xj = ring[j][0];
        const yj = ring[j][1];
        const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

        if (intersects) inside = !inside;
      }

      return inside;
    },
    getContainingPolygonCoordinates(geometry, point) {
      if (!geometry || !Array.isArray(geometry.coordinates)) return null;

      if (geometry.type === "Polygon") {
        return this.isPointInGeoJsonPolygon(point, geometry.coordinates) ? geometry.coordinates : null;
      }

      if (geometry.type === "MultiPolygon") {
        return (
          geometry.coordinates.find((polygon) => this.isPointInGeoJsonPolygon(point, polygon)) || null
        );
      }

      return null;
    },
    getMapFeatureKey(feature, polygonCoordinates) {
      const properties = feature.properties || {};
      const base = properties.kode_kecamatan || properties.kabkot || properties.id || "";
      const center = this.getGeoJsonGeometryCenter({
        type: "Polygon",
        coordinates: polygonCoordinates,
      });

      return `${base}-${center ? `${center.lat.toFixed(5)}-${center.lng.toFixed(5)}` : ""}`;
    },
    createCellCoordinates(polygonCoordinates, marker, markers) {
      const exteriorRing = polygonCoordinates && polygonCoordinates[0];
      if (!Array.isArray(exteriorRing) || exteriorRing.length < 4) return null;

      let cell = exteriorRing.map((coordinate) => ({
        lng: coordinate[0],
        lat: coordinate[1],
      }));

      markers.forEach((otherMarker) => {
        if (otherMarker.id === marker.id || cell.length === 0) return;
        cell = this.clipCellByCloserCenter(cell, marker, otherMarker);
      });

      if (cell.length < 3) return null;

      const ring = cell.map((point) => [point.lng, point.lat]);
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([first[0], first[1]]);
      }

      return [ring];
    },
    clipCellByCloserCenter(points, marker, otherMarker) {
      const clipped = [];

      for (let index = 0; index < points.length; index += 1) {
        const current = points[index];
        const previous = points[(index + points.length - 1) % points.length];
        const currentInside = this.isPointCloserToMarker(current, marker, otherMarker);
        const previousInside = this.isPointCloserToMarker(previous, marker, otherMarker);

        if (currentInside) {
          if (!previousInside) {
            clipped.push(this.getBisectorIntersection(previous, current, marker, otherMarker));
          }
          clipped.push(current);
        } else if (previousInside) {
          clipped.push(this.getBisectorIntersection(previous, current, marker, otherMarker));
        }
      }

      return clipped.filter(Boolean);
    },
    isPointCloserToMarker(point, marker, otherMarker) {
      return (
        this.getSquaredDistance(point, marker) <= this.getSquaredDistance(point, otherMarker) + 0.000000000001
      );
    },
    getSquaredDistance(point, marker) {
      const lngDelta = point.lng - marker.lng;
      const latDelta = point.lat - marker.lat;

      return lngDelta * lngDelta + latDelta * latDelta;
    },
    getBisectorIntersection(start, end, marker, otherMarker) {
      const startDistance = this.getSquaredDistance(start, marker) - this.getSquaredDistance(start, otherMarker);
      const endDistance = this.getSquaredDistance(end, marker) - this.getSquaredDistance(end, otherMarker);
      const ratio = startDistance / (startDistance - endDistance);

      if (!Number.isFinite(ratio)) return null;

      return {
        lng: start.lng + (end.lng - start.lng) * ratio,
        lat: start.lat + (end.lat - start.lat) * ratio,
      };
    },
    getProvinceGeoJsonFileName(province) {
      const normalizedProvince = this.normalizeFileName(province);
      const specialFiles = {
        "di yogyakarta": "Di Yogyakarta",
        "dki jakarta": "Dki Jakarta",
        "kepulauan bangka belitung": "Kepulauan Bangka Belitung",
        "kepulauan riau": "Kepulauan riau",
      };

      if (specialFiles[normalizedProvince]) return specialFiles[normalizedProvince];

      return normalizedProvince
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    normalizeFileName(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    },
    normalizeRegionName(value) {
      return this.normalizeFileName(value)
        .replace(/\b(kota|kabupaten|kab|administrasi)\b/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },
    async fitDistributionMap() {
      if (!window.google) return;

      const map = await this.getDistributionMap();
      if (!map) return;

      const bounds = new window.google.maps.LatLngBounds();
      let hasBounds = false;

      this.filteredMarkers.forEach((marker) => {
        if (marker.bucketKey && !this.isMapLegendChecked(marker.bucketKey)) return;
        bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
        hasBounds = true;
      });

      this.circleOverlays.forEach((circle) => {
        if (!this.isMapLegendChecked(circle.bucketKey) || !circle.getBounds()) return;
        bounds.union(circle.getBounds());
        hasBounds = true;
      });

      if (this.geoJsonLayer) {
        this.geoJsonLayer.forEach((feature) => {
          const bucketKey = feature.getProperty("_bucketKey");
          if (!bucketKey || this.isMapLegendChecked(bucketKey)) {
            hasBounds = this.extendBoundsFromGeometry(feature.getGeometry(), bounds) || hasBounds;
          }
        });
      }

      if (hasBounds) {
        map.fitBounds(bounds);
        if (this.filteredMarkers.length === 1) map.setZoom(13);
      }
    },
    extendBoundsFromGeometry(geometry, bounds) {
      if (!geometry) return false;

      if (geometry.getType && geometry.getType() === "Point") {
        bounds.extend(geometry.get());
        return true;
      }

      if (!geometry.getArray) return false;

      return geometry.getArray().reduce((hasBounds, item) => {
        if (item && typeof item.lat === "function" && typeof item.lng === "function") {
          bounds.extend(item);
          return true;
        }

        return this.extendBoundsFromGeometry(item, bounds) || hasBounds;
      }, false);
    },
    openDropdown(type) {
      if (type === "regency" && !this.filter.province) return;
      if (type === "district" && !this.filter.regency) return;
      this.openDropdowns[type] = true;
    },
    toggleDropdown(type) {
      if (type === "regency" && !this.filter.province) return;
      if (type === "district" && !this.filter.regency) return;
      this.openDropdowns[type] = !this.openDropdowns[type];
    },
    handleSearchInput(type) {
      this.openDropdown(type);
      if (type === "domain") {
        this.filter.domain = this.searchInputs.domain;
      } else if (type === "province") {
        if (this.filter.province && this.searchInputs.province !== this.filter.province) {
          this.filter.province = null;
          this.selectedProvinceCode = null;
          this.filter.regency = null;
          this.selectedRegencyCode = null;
          this.filter.district = null;
          this.selectedDistrict = null;
          this.searchInputs.regency = "";
          this.searchInputs.district = "";
          this.regencyOptions = [];
          this.districtOptions = [];
          this.onDistrictSelect(null);
        }
      } else if (type === "regency") {
        if (this.filter.regency && this.searchInputs.regency !== this.filter.regency) {
          this.filter.regency = null;
          this.selectedRegencyCode = null;
          this.filter.district = null;
          this.selectedDistrict = null;
          this.searchInputs.district = "";
          this.districtOptions = [];
          this.onDistrictSelect(null);
        }
      } else if (type === "district") {
        if (this.filter.district && this.searchInputs.district !== this.filter.district) {
          this.filter.district = null;
          this.selectedDistrict = null;
          this.onDistrictSelect(null);
        }
      }
    },
    selectDomainOption(opt) {
      this.filter.domain = opt;
      this.searchInputs.domain = opt;
      this.openDropdowns.domain = false;
      if (this.selectedDistrict) {
        this.fetchRadiusDistribution(this.selectedDistrict);
        this.fetchMapDistribution(this.selectedDistrict);
      }
    },
    selectProvinceOption(opt) {
      const label = typeof opt === "object" ? opt.label : opt;
      const value = typeof opt === "object" ? opt.value : opt;

      this.filter.province = label;
      this.selectedProvinceCode = value;
      this.searchInputs.province = label;
      this.openDropdowns.province = false;

      this.filter.regency = null;
      this.selectedRegencyCode = null;
      this.searchInputs.regency = "";
      this.regencyOptions = [];

      this.filter.district = null;
      this.selectedDistrict = null;
      this.searchInputs.district = "";
      this.districtOptions = [];

      this.onDistrictSelect(null);
      this.fetchRegencyOptions(value);
    },
    selectRegencyOption(opt) {
      const label = typeof opt === "object" ? opt.label : opt;
      const value = typeof opt === "object" ? opt.value : opt;

      this.filter.regency = label;
      this.selectedRegencyCode = value;
      this.searchInputs.regency = label;
      this.openDropdowns.regency = false;

      this.filter.district = null;
      this.selectedDistrict = null;
      this.searchInputs.district = "";
      this.districtOptions = [];

      this.onDistrictSelect(null);
      this.fetchDistrictOptions(value);
    },
    selectDistrictOption(opt) {
      const label = typeof opt === "object" ? opt.label : opt;

      this.filter.district = label;
      this.searchInputs.district = label;
      this.openDropdowns.district = false;

      this.onDistrictSelect(opt);
    },
    handleDocumentClick(event) {
      const keys = ["domain", "province", "regency", "district", "status"];
      keys.forEach((key) => {
        const dropdown = this.$refs[key + "Dropdown"];
        if (dropdown && !dropdown.contains(event.target)) {
          this.openDropdowns[key] = false;
        }
      });
    },
    onDistrictSelect(val) {
      this.activeRingModal = null;
      if (!val) {
        this.selectedDistrict = null;
        this.radiusRows = [];
        this.domainRows = [];
        this.isDomainLoading = false;
        this.markers = [];
        this.radiusCenter = null;
        this.mapFeatureLabels = [];
        this.isMapLoading = false;
        this.mapRequestToken += 1;
        this.clearGeoJsonLayer();
        this.clearCircleOverlays();
        return;
      }

      const match = typeof val === "object"
        ? val
        : this.districtOptions.find(
          (d) => d.label === val || d.value === val
        );

      if (match) {
        this.selectDistrict(match);
      } else {
        this.selectedDistrict = typeof val === "object" ? val.value : val;
        this.fetchRadiusDistribution(val);
        this.fetchMapDistribution(val);
        this.fetchDomainList(val);
      }
    },
    resetFilter() {
      if (!this.hasActiveDistrictFilter) return;
      this.activeRingModal = null;
      this.filter.domain = "All Domains";
      this.filter.province = null;
      this.selectedProvinceCode = null;
      this.filter.regency = null;
      this.selectedRegencyCode = null;
      this.filter.district = null;
      this.filter.domain_status = null;

      this.searchInputs.domain = "All Domains";
      this.searchInputs.province = "";
      this.searchInputs.regency = "";
      this.searchInputs.district = "";

      this.openDropdowns.domain = false;
      this.openDropdowns.province = false;
      this.openDropdowns.regency = false;
      this.openDropdowns.district = false;
      this.openDropdowns.status = false;

      this.regencyOptions = [];
      this.districtOptions = [];
      this.selectedDistrict = null;
      this.districtSearch = "";
      this.domainListSearch = "";
      this.radiusRows = [];
      this.domainRows = [];
      this.isDomainLoading = false;
      this.isRadiusLoading = false;
      this.radiusRequestToken += 1;
      this.markers = [];
      this.radiusCenter = null;
      this.mapFeatureLabels = [];
      this.isMapLoading = false;
      this.mapRequestToken += 1;
      this.clearGeoJsonLayer();
      this.clearCircleOverlays();
      this.showDistrictDropdown = false;
    },
    async applyFilterFromQuery() {
      const { district, regency, province } = this.$route.query;
      if (!district && !regency && !province) return;
      this.isApplyingFromQuery = true;

      try {
        if (this.provinceOptions.length === 0) {
          await this.fetchProvinceOptions();
        }

        if (province) {
          const matchProv = this.provinceOptions.find(
            (p) => p.label === province || p.value === province
          );
          if (matchProv) {
            this.filter.province = matchProv.label;
            this.selectedProvinceCode = matchProv.value;
            this.searchInputs.province = matchProv.label;
            await this.fetchRegencyOptions(matchProv.value);
          } else {
            this.filter.province = province;
            this.selectedProvinceCode = province;
            this.searchInputs.province = province;
          }
        }

        if (regency) {
          const matchReg = this.regencyOptions.find(
            (r) => r.label === regency || r.value === regency
          );
          if (matchReg) {
            this.filter.regency = matchReg.label;
            this.selectedRegencyCode = matchReg.value;
            this.searchInputs.regency = matchReg.label;
            await this.fetchDistrictOptions(matchReg.value);
          } else {
            this.filter.regency = regency;
            this.selectedRegencyCode = regency;
            this.searchInputs.regency = regency;
          }
        }

        if (district) {
          this.filter.district = district;
          this.searchInputs.district = district;
          const matchDist = this.districtOptions.find(
            (d) => d.label === district || d.value === district
          );
          if (matchDist) {
            this.selectDistrict(matchDist);
          } else {
            this.onDistrictSelect(district);
          }
        }
      } catch (err) {
        // ignore errors during query filter applying
      } finally {
        this.isApplyingFromQuery = false;
      }
    },
  },
};
</script>

<style scoped>
.domain-distribution-page {
  color: #4b4b4b;
  min-height: calc(100vh - 48px);
}

.page-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-heading h1 {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.latest-update {
  display: flex;
  align-items: center;
  color: #4B4B4B;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 100%;
  letter-spacing: 0;
  gap: 8px;
}

.latest-update i {
  color: #b5b5b5;
  font-size: 12px;
}

.domain-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 1.5px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  padding: 16px;
}

.domain-card h2 {
  color: #4b4b4b;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.district-filter-card {
  padding: 12px 16px;
}

.district-filter-row {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  margin-top: 12px;
}

.filter-multiselect-group {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  flex: 1;
  gap: 12px;
  min-width: 0;
}

.district-dropdown {
  position: relative;
  width: 100%;
  min-width: 0;
}

.district-dropdown-control {
  align-items: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  height: 28px;
  overflow: hidden;
}

.district-dropdown-control.active {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.district-dropdown-control.disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.district-dropdown-control input {
  border: 0;
  color: #4b4b4b;
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  height: 100%;
  min-width: 0;
  outline: none;
  padding: 4px 10px;
}

.district-dropdown-control input:disabled {
  background: #f5f5f5;
  color: #bdbdbd;
  cursor: not-allowed;
}

.district-dropdown-control input:disabled::placeholder {
  color: #bdbdbd;
}

.district-chevron {
  align-items: center;
  background: transparent;
  border: 0;
  color: #4b4b4b;
  display: flex;
  height: 100%;
  justify-content: center;
  padding: 0 10px;
}

.district-chevron:disabled {
  color: #bdbdbd;
  cursor: not-allowed;
  opacity: 1;
}

.district-dropdown-menu {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  left: 0;
  max-height: 214px;
  overflow-y: auto;
  padding: 8px 0;
  position: absolute;
  right: 0;
  top: 28px;
  z-index: 20;
}

.district-dropdown-item {
  background: transparent;
  border: 0;
  color: #4b4b4b;
  display: block;
  font-size: 14px;
  line-height: 1.25;
  padding: 8px 16px;
  text-align: left;
  width: 100%;
}

.district-dropdown-item:hover {
  background: #f7f7f7;
}

.district-dropdown-empty {
  color: #9d9d9d;
  font-size: 14px;
  padding: 8px 16px;
}

.reset-filter-btn {
  align-items: center;
  background: #eaeaea;
  border: 0;
  border-radius: 4px;
  color: #9d9d9d;
  cursor: not-allowed;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  gap: 8px;
  height: 28px;
  padding: 4px 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.reset-filter-btn.active {
  background: #2f80ed;
  color: #fff;
  cursor: pointer;
}

.radius-table-card {
  min-height: 256px;
}

.radius-table-card h2 {
  padding-bottom: 16px;
}

.card-header-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
}

.card-header-row h2 {
  padding-bottom: 0;
}

.domain-header-controls {
  align-items: center;
  display: flex;
  gap: 12px;
}

.status-filter-dropdown {
  position: relative;
  width: 160px;
}

.status-filter-dropdown .district-dropdown-control {
  cursor: pointer;
}

.status-filter-dropdown .status-select-input {
  cursor: pointer;
  user-select: none;
}

.domain-search-wrapper {
  height: 28px;
  position: relative;
  width: 290px;
}

.domain-search-input {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #4b4b4b;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  height: 28px;
  letter-spacing: 0;
  line-height: 100%;
  outline: none;
  padding: 4px 30px 4px 10px;
  width: 290px;
}

.domain-search-input::placeholder {
  color: #9d9d9d;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 100%;
}

.domain-search-icon {
  color: #9d9d9d;
  font-size: 12px;
  pointer-events: none;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.radius-table-wrapper {
  border-top: 1px solid #e5e5e5;
  overflow-x: auto;
}

.radius-table {
  border-collapse: collapse;
  width: 100%;
}

.radius-table th {
  border-bottom: 1px solid #e5e5e5;
  color: #9d9d9d;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 100%;
  padding: 16px 0;
  text-align: left;
}

.radius-table td {
  color: #000000;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.4;
  padding: 12px 0;
}

.radius-table td.domain-cell {
  word-break: break-all;
  overflow-wrap: anywhere;
  max-width: 300px;
  min-width: 160px;
}

.risk-badge {
  align-items: center;
  border-radius: 10px;
  color: #ffffff;
  cursor: default;
  display: inline-flex;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500 !important;
  justify-content: center;
  line-height: 16.5px;
  letter-spacing: 0.06px;
  padding: 4px 12px;
  pointer-events: none;
  user-select: none;
}

.risk-badge-high {
  background: var(--Colors-Error-600, #D92D20);
  color: #ffffff;
  font-weight: 500 !important;
}

.risk-badge-medium {
  background: var(--Colors-Warning-400, #FDB022);
  color: #ffffff;
  font-weight: 500 !important;
}

.risk-badge-low {
  background: #12b76a;
  color: #ffffff;
  font-weight: 500 !important;
}

.status-badge {
  align-items: center;
  border-radius: 10px;
  cursor: default;
  display: inline-flex;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500 !important;
  justify-content: center;
  line-height: 16.5px;
  letter-spacing: 0.06px;
  padding: 4px 12px;
  pointer-events: none;
  user-select: none;
}

.status-badge-under-review {
  background: var(--Colors-Orange-dark-25, #FFF9F5);
  color: var(--Orange-200, #EC911B);
  font-weight: 500 !important;
}

.status-badge-watchlisted {
  background: var(--Colors-Error-25, #FFFBFA);
  color: var(--Colors-Error-500, #F04438);
  font-weight: 500 !important;
}

.action-cell {
  align-items: center;
  display: flex;
  gap: 8px;
}

.btn-action-watchlist {
  align-items: center;
  background: #267CE5;
  border: none;
  border-radius: 8px;
  color: #FFFFFF;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  justify-content: center;
  line-height: 100%;
  padding: 6px 12px;
  transition: background-color 0.2s ease;
}

.btn-action-watchlist:hover {
  background: #1e66c4;
}

.btn-action-detail {
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  color: #4B4B4B;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  justify-content: center;
  line-height: 100%;
  padding: 6px 12px;
  transition: all 0.2s ease;
}

.btn-action-detail:hover {
  background: #f8f9fa;
  border-color: #d0d0d0;
}

.radius-table .empty-state {
  color: #9d9d9d;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  height: 178px;
  letter-spacing: 0;
  line-height: 100%;
  text-align: center;
  vertical-align: middle;
}

.items-per-page-text {
  color: #cbcbcb;
  font-size: 14px;
  margin-left: 8px;
}

.pagination-minimal .pagination .page-item .page-link {
  border: 1px solid #dee2e6;
  color: #4b4b4b;
  font-size: 16px;
  line-height: 1;
  padding: 4px 10px;
}

.pagination-minimal .pagination .page-item.disabled .page-link {
  background-color: #fff;
  border-color: #dee2e6;
  color: #6c757d;
  pointer-events: none;
}

.ring-modal-sidebar {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  height: calc(100% - 32px);
  position: absolute;
  right: 16px;
  top: 16px;
  width: 350px;
  z-index: 100;
  overflow: hidden;
}

.ring-modal-header {
  border-bottom: 1px solid #f0f0f0;
  padding: 16px 16px 12px;
}

.ring-modal-title-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.ring-modal-title-left {
  align-items: center;
  display: flex;
  gap: 8px;
}

.ring-color-dot {
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  height: 10px;
  width: 10px;
}

.ring-modal-title {
  color: #111827;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
}

.ring-modal-close-btn {
  background: transparent;
  border: 0;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  transition: color 0.2s ease;
}

.ring-modal-close-btn:hover {
  color: #111827;
}

.ring-modal-subtitle {
  color: #6b7280;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  margin-top: 4px;
}

.ring-modal-search-box {
  align-items: center;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  height: 36px;
  margin-top: 12px;
  padding: 0 12px;
}

.ring-modal-search-icon {
  color: #9ca3af;
  font-size: 13px;
  margin-right: 8px;
}

.ring-modal-search-input {
  background: transparent;
  border: 0;
  color: #111827;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  width: 100%;
}

.ring-modal-search-input::placeholder {
  color: #9ca3af;
}

.ring-modal-sort-row {
  align-items: center;
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: 11.44px;
  gap: 6px;
  margin-top: 10px;
}

.ring-modal-sort-label {
  color: #6b7280;
  font-size: 11.44px;
}

.ring-modal-sort-btn {
  background: transparent;
  border: 0;
  color: #267CE5;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 11.44px;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 0;
  padding: 0;
  user-select: none;
}

.ring-modal-sort-btn:hover {
  opacity: 0.8;
}

.ring-modal-body {
  flex: 1;
  overflow-y: auto;
}

.ring-modal-loading,
.ring-modal-empty {
  color: #6b7280;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  padding: 24px;
  text-align: center;
}

.ring-modal-list {
  display: flex;
  flex-direction: column;
}

.ring-modal-item {
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
}

.ring-modal-item:last-child {
  border-bottom: none;
}

.ring-modal-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ring-modal-domain-name {
  color: #111827;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  word-break: break-all;
}

.ring-modal-domain-scans {
  color: #9ca3af;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
}

.status-badge-text {
  color: #4b4b4b;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 500 !important;
  line-height: 16.5px;
  letter-spacing: 0.06px;
}

.fade-slide-left-enter-active,
.fade-slide-left-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-left-enter,
.fade-slide-left-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.map-card {
  padding: 12px 16px 16px;
}

.map-card h2 {
  font-size: 14px;
  line-height: 1.2;
  margin-bottom: 14px;
}

.map-wrapper {
  height: 528px;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.distribution-map {
  height: 528px;
  width: 100%;
}

.map-wrapper--no-data ::v-deep .gm-bundled-control,
.map-wrapper--no-data ::v-deep .gm-fullscreen-control,
.map-wrapper--no-data ::v-deep .gm-svpc,
.map-wrapper--no-data ::v-deep .gm-control-active {
  display: none !important;
}

.map-state {
  background: rgba(255, 255, 255, 0.92);
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
  color: #666;
  font-size: 13px;
  left: 50%;
  padding: 8px 12px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
}

.map-legend {
  align-items: center;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 8px;
  bottom: 16px;
  box-shadow: 0 2px 10px rgba(36, 46, 66, 0.16);
  display: flex;
  gap: 26px;
  left: 18px;
  min-height: 82px;
  padding: 14px 18px;
  position: absolute;
  z-index: 5;
}

.map-legend.collapsed {
  border-radius: 0 6px 6px 0;
  bottom: 16px;
  box-shadow: 1px 2px 8px rgba(36, 46, 66, 0.16);
  gap: 0;
  left: 0;
  min-height: 82px;
  padding: 0;
  width: 42px;
}

.map-legend-grid {
  display: grid;
  grid-auto-flow: column;
  gap: 9px 22px;
  grid-template-columns: repeat(2, max-content);
  grid-template-rows: repeat(3, max-content);
}

.map-legend-item {
  align-items: center;
  background: transparent;
  border: 0;
  color: #686868;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 500;
  gap: 9px;
  line-height: 1;
  padding: 0;
  white-space: nowrap;
}

.map-legend-item:not(.checked) {
  color: #a3a3a3;
}

.map-legend-item:not(.checked) .legend-color {
  opacity: 0.35;
}

.legend-check {
  align-items: center;
  background: #fff;
  border: 1px solid #b8c7dc;
  border-radius: 3px;
  color: #2f80ed;
  display: flex;
  height: 12px;
  justify-content: center;
  line-height: 1;
  width: 12px;
}

.map-legend-item.checked .legend-check::after {
  color: #2f80ed;
  content: "\2713";
  font-size: 10px;
  font-weight: 700;
}

.legend-color {
  border-radius: 3px;
  display: inline-block;
  height: 10px;
  width: 10px;
}

.legend-collapse {
  align-items: center;
  background: transparent;
  border: 0;
  color: #a8a8a8;
  display: flex;
  font-size: 18px;
  height: 42px;
  justify-content: center;
  line-height: 1;
  outline: none;
  padding: 0;
  width: 14px;
}

.legend-collapse:focus,
.legend-collapse:active {
  box-shadow: none;
  outline: none;
}

.legend-collapse i {
  line-height: 1;
}

.map-legend.collapsed .legend-collapse {
  color: #3d3d3d;
  font-size: 18px;
  height: 100%;
  width: 100%;
}

@media (max-width: 767px) {

  .page-heading,
  .district-filter-row {
    align-items: stretch;
    flex-direction: column;
  }

  .filter-multiselect-group {
    grid-template-columns: 1fr;
  }

  .latest-update {
    font-size: 12px;
  }

  .reset-filter-btn {
    justify-content: center;
    width: 100%;
  }

  .map-legend {
    align-items: flex-start;
    flex-direction: column;
    max-width: calc(100% - 32px);
  }

  .map-legend-grid {
    grid-auto-flow: row;
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }

  .legend-collapse {
    display: flex;
  }
}
</style>
